import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ShoppingBag,
  X,
  Plus,
  Minus,
  Trash2,
  ArrowRight,
} from 'lucide-react';

// ==============================
// SINGLE PRODUCT CARD
// ==============================
const SingleProductCard = ({ product, onAddToCart }) => {
  const initialQty =
    product.tiers && product.tiers.length > 0
      ? product.tiers[0].qty
      : 1;

  const [quantity, setQuantity] = useState(initialQty);

  const getPrice = () => {
    if (!product.tiers || product.tiers.length === 0) {
      return product.basePrice;
    }

    const tier = product.tiers.find(
      (t) => Number(t.qty) === Number(quantity)
    );

    return tier ? Number(tier.price) : product.basePrice;
  };

  const finalPrice = getPrice();

  const handleAdd = () => {
    const unitPrice = finalPrice / quantity;

    const itemToAdd = {
      id: product._id || product.id,
      name: product.title,
      price: unitPrice,
      qty: quantity,
      image: product.image,
      specs: product.description,
      totalTierPrice: finalPrice,
    };

    onAddToCart(itemToAdd);
  };

  return (
    <div className="w-[85%] sm:w-[48%] lg:w-[32%] xl:w-[23%] flex-shrink-0 snap-start h-full">
      <div className="group relative overflow-hidden rounded-[2rem] bg-white border border-gray-100 shadow-[0_10px_35px_rgba(0,0,0,0.05)] hover:shadow-[0_25px_60px_rgba(0,0,0,0.12)] transition-all duration-500 h-full flex flex-col">
        
        {/* IMAGE */}
        <div className="relative h-60 sm:h-64 overflow-hidden bg-gray-100">
          <img
            src={product.image}
            alt={product.title}
            className="w-full h-full object-cover group-hover:scale-110 transition duration-700"
          />

          <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />

          <div className="absolute top-4 left-4">
            <span className="bg-white/90 backdrop-blur-md text-black text-[11px] font-bold px-3 py-1 rounded-full shadow">
              BULK ORDER
            </span>
          </div>
        </div>

        {/* CONTENT */}
        <div className="p-5 sm:p-6 flex flex-col flex-1">
          
          {/* FIXED ALIGNMENT */}
          <div className="min-h-[120px] flex flex-col">
            <h3 className="text-lg sm:text-xl font-black text-gray-900 leading-tight line-clamp-2 min-h-[56px]">
              {product.title}
            </h3>

            <p className="text-sm text-gray-500 mt-2 leading-relaxed line-clamp-2 min-h-[44px]">
              {product.description}
            </p>
          </div>

          {/* PRICE + SELECT */}
          <div className="mt-auto pt-6 flex items-end justify-between gap-3">
            <div>
              <p className="text-[11px] uppercase tracking-widest text-gray-400 font-bold">
                Price
              </p>

              <h2 className="text-2xl sm:text-3xl font-black text-black">
                ₹{finalPrice}
              </h2>
            </div>

            <select
              value={quantity}
              onChange={(e) => setQuantity(Number(e.target.value))}
              className="bg-gray-100 border border-gray-200 rounded-2xl px-4 py-3 text-sm font-semibold outline-none cursor-pointer hover:bg-gray-200 transition"
            >
              {product.tiers?.map((tier) => (
                <option key={tier.qty} value={tier.qty}>
                  Qty {tier.qty}
                </option>
              )) || <option value={1}>Qty 1</option>}
            </select>
          </div>

          {/* BUTTON */}
          <button
            onClick={handleAdd}
            className="mt-6 w-full bg-black text-white rounded-2xl py-4 font-bold flex items-center justify-center gap-2 hover:bg-yellow-500 hover:text-black transition-all duration-300 active:scale-[0.98]"
          >
            Add to Bag
            <ArrowRight size={18} />
          </button>
        </div>
      </div>
    </div>
  );
};

// ==============================
// MAIN COMPONENT
// ==============================
const ProductCard = () => {
  const navigate = useNavigate();

  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState([]);
  const [isBagOpen, setIsBagOpen] = useState(false);

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const res = await fetch(
        'https://dgl-core-9x7.dailygolive.in/api/products'
      );

      const data = await res.json();

      const formatted = data.map((item) => ({
        ...item,
        title: item.name,
        basePrice: Number(item.basePrice || 0),
        tiers: (item.tiers || []).map((t) => ({
          qty: Number(t.qty),
          price: Number(t.price),
        })),
      }));

      // SHOW ONLY 8 PRODUCTS
      setProducts(formatted.slice(0, 8));
    } catch (err) {
      console.error(err);
    }
  };

  const addToCart = (item) => {
    setCart((prevCart) => {
      const existingItem = prevCart.find((i) => i.id === item.id);

      if (existingItem) {
        return prevCart.map((i) =>
          i.id === item.id ? item : i
        );
      }

      return [...prevCart, item];
    });

    setIsBagOpen(true);
  };

  const removeFromCart = (id) => {
    setCart((prev) => prev.filter((item) => item.id !== id));
  };

  const updateBagQty = (id, delta) => {
    setCart((prev) =>
      prev.map((item) => {
        if (item.id === id) {
          const newQty = item.qty + delta;

          return {
            ...item,
            qty: newQty > 0 ? newQty : 1,
          };
        }

        return item;
      })
    );
  };

  const cartTotal = cart.reduce(
    (sum, item) => sum + item.price * item.qty,
    0
  );

  const handleCheckout = () => {
    navigate('/PaymentIndex', {
      state: {
        items: cart,
        total: cartTotal,
      },
    });
  };

  return (
    <div className="min-h-screen bg-[#f6f7fb] w-full overflow-x-hidden">
      
      {/* HERO */}
      <section className="w-full px-4 sm:px-6 lg:px-8 pt-10 sm:pt-14 pb-10">
        <div className="w-full">
        </div>
      </section>

      {/* PRODUCTS */}
      <section className="w-full px-4 sm:px-6 lg:px-8 pb-24">
        <div className="w-full">
          
          {/* TOP */}
          <div className="flex items-end justify-between mb-8 gap-4 flex-wrap">
            <div>
              <h2 className="text-3xl sm:text-4xl font-black text-gray-900">
                Featured Products
              </h2>

              <p className="text-gray-500 mt-2 text-sm sm:text-base">
                Optimized for mobile-first bulk ordering.
              </p>
            </div>

            <div className="hidden md:flex items-center gap-2 bg-white border border-gray-200 rounded-2xl px-5 py-3 shadow-sm">
              <ShoppingBag size={18} />
              <span className="font-bold text-sm">
                {cart.length} Items
              </span>
            </div>
          </div>

          {/* HORIZONTAL SCROLL */}
          <div className="flex gap-6 overflow-x-auto scroll-smooth snap-x pb-4 no-scrollbar w-full">
            {products.map((product) => (
              <SingleProductCard
                key={product._id}
                product={product}
                onAddToCart={addToCart}
              />
            ))}
          </div>
        </div>
      </section>

      {/* FLOATING CART */}
      <button
        onClick={() => setIsBagOpen(true)}
        className="fixed bottom-5 right-5 sm:bottom-8 sm:right-8 z-50 bg-black text-white p-4 sm:p-5 rounded-full shadow-[0_20px_50px_rgba(0,0,0,0.25)] hover:scale-110 transition-all active:scale-95"
      >
        <ShoppingBag size={24} />

        {cart.length > 0 && (
          <span className="absolute -top-1 -right-1 bg-yellow-500 text-black text-[10px] font-black w-6 h-6 rounded-full flex items-center justify-center border-2 border-white animate-pulse">
            {cart.length}
          </span>
        )}
      </button>

      {/* CART SIDEBAR */}
      {isBagOpen && (
        <div className="fixed inset-0 z-[100] flex justify-end">
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setIsBagOpen(false)}
          />

          <div className="relative w-full sm:max-w-md bg-white h-full shadow-2xl flex flex-col animate-slideIn">
            
            {/* HEADER */}
            <div className="p-6 border-b bg-white sticky top-0 z-10">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-black text-gray-900 flex items-center gap-2">
                    <ShoppingBag size={22} />
                    Your Bag
                  </h2>

                  <p className="text-xs text-gray-400 mt-1 font-semibold uppercase tracking-widest">
                    {cart.length} Products Added
                  </p>
                </div>

                <button
                  onClick={() => setIsBagOpen(false)}
                  className="w-11 h-11 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition"
                >
                  <X size={22} />
                </button>
              </div>
            </div>

            {/* ITEMS */}
            <div className="flex-1 overflow-y-auto p-5 space-y-5">
              {cart.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-center">
                  <div className="w-24 h-24 rounded-full bg-gray-100 flex items-center justify-center">
                    <ShoppingBag size={40} className="text-gray-300" />
                  </div>

                  <h3 className="mt-5 text-lg font-bold text-gray-800">
                    Your bag is empty
                  </h3>

                  <p className="text-sm text-gray-400 mt-2 max-w-xs">
                    Add products to continue your bulk purchase.
                  </p>
                </div>
              ) : (
                cart.map((item) => (
                  <div
                    key={item.id}
                    className="bg-[#f8f9fc] border border-gray-100 rounded-[2rem] p-4 flex gap-4"
                  >
                    <img
                      src={item.image}
                      alt={item.name}
                      className="w-24 h-24 object-cover rounded-2xl"
                    />

                    <div className="flex-1 flex flex-col justify-between">
                      <div>
                        <div className="flex justify-between gap-2">
                          <h3 className="font-black text-gray-900 leading-tight text-sm">
                            {item.name}
                          </h3>

                          <button
                            onClick={() => removeFromCart(item.id)}
                            className="text-gray-300 hover:text-red-500 transition"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>

                        <p className="text-xs text-gray-400 mt-1">
                          Premium Bulk Product
                        </p>
                      </div>

                      <div className="flex items-center justify-between mt-4">
                        
                        {/* QTY */}
                        <div className="flex items-center bg-white rounded-2xl border border-gray-200 px-2 py-2 gap-4">
                          <button
                            onClick={() =>
                              updateBagQty(item.id, -1)
                            }
                            className="text-gray-400 hover:text-black"
                          >
                            <Minus size={15} />
                          </button>

                          <span className="font-bold text-sm min-w-[20px] text-center">
                            {item.qty}
                          </span>

                          <button
                            onClick={() =>
                              updateBagQty(item.id, 1)
                            }
                            className="text-gray-400 hover:text-black"
                          >
                            <Plus size={15} />
                          </button>
                        </div>

                        {/* PRICE */}
                        <h3 className="text-lg font-black text-black">
                          ₹
                          {(item.price * item.qty).toLocaleString()}
                        </h3>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* FOOTER */}
            {cart.length > 0 && (
              <div className="border-t bg-white p-6 space-y-5">
                <div className="bg-[#f7f8fb] rounded-3xl p-5">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-xs uppercase tracking-widest text-gray-400 font-bold">
                        Total Amount
                      </p>

                      <h2 className="text-3xl font-black text-black mt-1">
                        ₹{cartTotal.toLocaleString()}
                      </h2>
                    </div>

                    <div className="w-14 h-14 rounded-2xl bg-black text-white flex items-center justify-center">
                      <ShoppingBag size={24} />
                    </div>
                  </div>
                </div>

                <button
                  onClick={handleCheckout}
                  className="w-full bg-black text-white py-5 rounded-[1.5rem] font-black text-lg hover:bg-yellow-500 hover:text-black transition-all duration-300 active:scale-[0.98]"
                >
                  Checkout Now
                </button>

                <p className="text-center text-[11px] text-gray-400 font-semibold uppercase tracking-widest">
                  Secure Checkout Powered By Daily Go
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* STYLES */}
      <style>{`
        .no-scrollbar::-webkit-scrollbar {
          display: none;
        }

        .no-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }

        @keyframes slideIn {
          from {
            transform: translateX(100%);
          }
          to {
            transform: translateX(0%);
          }
        }

        .animate-slideIn {
          animation: slideIn 0.3s ease forwards;
        }
      `}</style>
    </div>
  );
};

export default ProductCard;